import { DataLoadError, Dropdown, ICONS, Loader } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import React, { ReactElement, useEffect } from 'react';
import { createSearchParams, Navigate, Outlet, useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';
import { DropdownItemProps } from 'semantic-ui-react';
import { ROUTES } from '../../Base.constants';
import SubHeader from '../../components/SubHeader';
import { SubheaderType } from '../../components/SubHeader/SubHeader.constants';
import { DEFAULT_MONITOR_SORT_FIELD } from '../../components/widgets/Dashboard/Monitor/Monitor.constants';
import { useUserLocalStorage } from '../../hooks/common';
import useFetchUserPermissions from '../../hooks/permissions/useFetchUserPermissions';
import useFetchWidgetMetrics from '../../hooks/riskProducts/dashboard/useFetchWidgetMetrics';
import useFetchRpColumns from '../../hooks/riskProducts/useFetchRpColumns';
import { useFetchWatchLists } from '../../hooks/watchLists';
import { WatchList } from '../../models/WatchList';
import { navigateToCompaniesList } from '../../utils/navigationUtils';
import { hasPermissionToRp } from '../../utils/permissionsUtils';
import { sortWatchlists } from '../../utils/watchListsUtils';
import { DEFAULT_SORT_ORDER } from '../Companies/Company.constants';
import NoContent from '../MyContent/NoContent/NoContent';
import { DashboardOutletContext } from './Dashboard/Dashboard';
import styles from './Dashboards.module.scss';

const cx = classNames.bind(styles);

const getDropdownOptions = (options: WatchList[]): DropdownItemProps[] =>
  options.map((option) => ({
    text: option.name,
    value: option.id,
    key: option.id,
    'data-testid': `watchlist-${option.id}`,
  }));

export const LastSelectedWatchListKey = 'LastSelectedWatchList';

const getDefaultSearchParams = () => {
  const searchParams = new URLSearchParams();
  searchParams.set('sortField', DEFAULT_MONITOR_SORT_FIELD);
  searchParams.set('sortOrder', DEFAULT_SORT_ORDER);
  return searchParams;
};

const Dashboards = (): ReactElement => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const isDashboardsRoute = useMatch(`${ROUTES.dashboards()}`);
  const { data: userPermissions } = useFetchUserPermissions();
  const { watchListId } = useParams<string>();
  const {
    data,
    isLoading: isWatchListsLoading,
    isError,
    refetch,
  } = useFetchWatchLists({ select: (data) => sortWatchlists(data) });
  const {
    data: rpColumns,
    isLoading: isRpColumnsLoading,
    isError: isRpColumnsError,
    refetch: refetchRpColumns,
  } = useFetchRpColumns();
  const {
    isLoading: isWidgetMetricsLoading,
    isError: isWidgetMetricsError,
    refetch: refetchWidgetMetrics,
  } = useFetchWidgetMetrics();
  const [lastSelectedWatchList, setLastSelectedWatchList] = useUserLocalStorage<string | null>(
    LastSelectedWatchListKey,
    null
  );

  const isLoading = isWatchListsLoading || isRpColumnsLoading || isWidgetMetricsLoading;
  const isWatchlistMatch = (watchListId?: string | null) =>
    watchListId && data && !!data.find((watchList) => watchList.id === watchListId);

  useEffect(() => {
    if (data && !isEmpty(data) && (isDashboardsRoute || !isWatchlistMatch(watchListId))) {
      navigate(
        {
          pathname: ROUTES.dashboards(
            isWatchlistMatch(lastSelectedWatchList) ? lastSelectedWatchList : data[0].id,
            true
          ),
          search: createSearchParams(getDefaultSearchParams()).toString(),
        },
        { replace: true }
      );
    }
  }, [isDashboardsRoute, watchListId, data, lastSelectedWatchList]);

  const onWatchListSelect = (watchListId: string) => {
    setLastSelectedWatchList(watchListId);
    navigate({ pathname: ROUTES.dashboards(watchListId, true), search }, { replace: true });
  };

  const onCreateWatchList = () => {
    navigateToCompaniesList(userPermissions, navigate);
  };

  const onRetry = () => {
    if (isError) {
      refetch();
    }
    if (isRpColumnsError) {
      refetchRpColumns();
    }
    if (isWidgetMetricsError) {
      refetchWidgetMetrics();
    }
  };

  const renderSubHeader = () => {
    if (isLoading) {
      return null;
    }
    return (
      <SubHeader title="Dashboard" routes={[]} type={SubheaderType.Dashboards}>
        {data && !isEmpty(data) && (
          <Dropdown
            className={cx('dropdown')}
            onOptionSelect={(watchListId) => {
              onWatchListSelect(watchListId as string);
            }}
            options={getDropdownOptions(data)}
            value={watchListId}
            dataTestId="watchlist-select"
            isLeftAligned
            withCaretIcon
            withCheckmarks
          />
        )}
      </SubHeader>
    );
  };
  const renderContent = () => {
    if (!isLoading && isEmpty(data)) {
      return (
        <NoContent
          dataTestId="no-watchlist-saved"
          header="No Watchlist yet"
          infoMessage="First create a watchlist with companies you are interested in. <br /> Then you can customize your dashboard to display the metrics that matter most to you."
          iconName={ICONS.NoContent}
          buttonParams={{
            ariaLabel: 'Create Watchlist',
            title: 'Create Watchlist',
            onClick: onCreateWatchList,
          }}
        />
      );
    }
    const outletContext: DashboardOutletContext = { isRpColumnsLoading, rpColumns: rpColumns ?? [] };
    return <Outlet context={outletContext} />;
  };

  if (!hasPermissionToRp(userPermissions)) {
    return <Navigate to={ROUTES.notFound} replace />;
  }

  if (isError || isRpColumnsError || isWidgetMetricsError) {
    return <DataLoadError onRetryClick={onRetry} />;
  }

  return (
    <div className={cx('body')}>
      {isLoading && <Loader position="center" />}
      {renderSubHeader()}
      {renderContent()}
    </div>
  );
};

export default Dashboards;
